import { AxiosError, AxiosResponse } from 'axios'
import axiosWrappedAgent from './AxiosInstanceWrapper'
import { GeneratedApiRoutes, ApiRoutesInterface } from './evertel-api-routes'
import { computed, action, makeObservable, observable, runInAction } from 'mobx'

// Known envs keep explicit mappings; any other name resolves by hostname convention:
// '{env}' -> https://{env}-api.evertelapp.com/api/ (development, demo, uat, e2e, ...).
// `string & {}` keeps literal autocomplete while allowing arbitrary env names.
export type APIEnvironments = 'staging' | 'local' | 'production' | (string & {})

class Api {

    private apiRoutes: GeneratedApiRoutes
    Routes: ApiRoutesInterface

    // Axios instance wrapper
    agent: typeof axiosWrappedAgent

    // Is production flag
    public isProduction = false

    // Used by direct media upload
    public baseURL = ''

    private _authToken = ''

    // Used to send x-app-build header
    private xAppBuild: string
    private xUserId = 0

    constructor(
        public env: APIEnvironments = 'staging',
        withCredentials: boolean,
        platform: string,
        buildNumber: string
    ) {
        this.agent = axiosWrappedAgent
        this.apiRoutes = new GeneratedApiRoutes(() => this.agent)
        this.Routes = this.apiRoutes.Routes

        this.setApiEnv(env)
        this.agent.setWithCredentials(withCredentials)

        this.xAppBuild = `${platform}/${buildNumber}`
        this.agent.setHeader('x-app-build', this.xAppBuild)

        this.agent.setHeader('x-user-id', '0')

        makeObservable(this, {
            env: observable,
            baseURL: observable,
            isProduction: observable,
            setAuthToken: action,
            setApiEnv: action,
            // @ts-expect-error don't care
            _authToken: observable,
            authToken: computed
        })
    }

    setApiEnv = (environment: APIEnvironments) => {
        runInAction(() => {
            this.env = environment
        })
        switch (environment) {
            case 'production': {
                this.isProduction = true
                this.setBaseURL('https://api.evertelapp.com/api/')
                break
            }

            case 'local': {
                this.isProduction = false
                this.setBaseURL('http://localhost:3000/api/')
                break
            }

            // Named environments (staging / development / demo / uat / e2e / ...) resolve by
            // convention: {env}.evertelapp.com -> https://{env}-api.evertelapp.com/api/.
            // Anything falsy falls back to staging (the historical default).
            default: {
                this.isProduction = false
                if (!environment) {
                    this.setBaseURL('https://staging-api.evertelapp.com/api/')
                    break
                }
                this.setBaseURL(`https://${environment}-api.evertelapp.com/api/`)
            }
        }
    }

    setBaseURL = (baseURL: string) => {
        this.baseURL = baseURL
        this.agent.setBaseURL(baseURL)
    }

    setAuthToken = (token: unknown) => {
        if (typeof token !== 'string' || token === '') {
            return
        }
        this._authToken = token
        this.agent.setHeader('Authorization', token)
    }

    get authToken () {
        return this._authToken
    }

    setUserId = (userId: number) => {
        this.xUserId = userId

        this.agent.setHeader('x-user-id', userId ? userId.toString() : null)
    }

    deleteAuthToken = () => {
        this.agent.setHeader('Authorization', undefined)
        this._authToken = ''
    }

    addRequestInterceptor(onFulfilled: unknown) {
        return this.agent.addRequestInterceptor(onFulfilled)
    }
    removeRequestInterceptor(interceptorId: number) {
        this.agent.removeRequestInterceptor(interceptorId)
    }

    addResponseInterceptor(
        onFulfilled: ((value: AxiosResponse) => void) | null,
        onRejected: ((error: AxiosError) => void) | null
    ) {
        return this.agent.addResponseInterceptor(onFulfilled, onRejected)
    }
    removeResponseInterceptor(interceptorId: number) {
        this.agent.removeResponseInterceptor(interceptorId)
    }

    get X_APP_BUILD() {
        return this.xAppBuild
    }

    get X_USER_ID() {
        return this.xUserId
    }
}

export { Api }
